import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { 
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/grade-courses/GradeCoursesAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { GradeCoursesFilter } from "./grade-courses-filter/GradeCoursesFilter";
import { GradeCoursesTable } from "./grade-courses-table/GradeCoursesTable";
import { useGradeCoursesUIContext } from "./GradeCoursesUIContext";

export function GradeCoursesCard() {
  // GradeCourses UI Context
  const coursesUIContext = useGradeCoursesUIContext();
  const coursesUIProps = useMemo(() => {
    return {
      ids: coursesUIContext.ids,
      setIds: coursesUIContext.setIds,
      queryParams: coursesUIContext.queryParams,
      openNewGradeCoursePage: coursesUIContext.openNewGradeCoursePage,
    };
  }, [coursesUIContext]);
    
  // Getting curret state of Grade Course list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.gradeCourses }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Grade Courses Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Sınıf Ders Listesini Excele Aktarır */
  const exportCoursesToExcel = () => {
    dispatch(actions.getAllGradeCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [10, 30, 15, 25]
      const columns = ["Sınıf", "Ders", "Kredisi", "Karnede Görünsün mü?"];
      entities.forEach(entity => rows.push([entity.grade, entity.course, entity.credit, entity.displayOnTheSchoolReport ? "Evet" : "Hayır"]));
      const filename = "Sınıf Ders Listesi";
      exportToExcel({ columns, rows, widths, filename });
    });    
  }

  /** Belli Bir Sınıf Ders Listesini PDF e Aktarır */
  const exportCoursesToPDF = () => {
    dispatch(actions.getAllGradeCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Ders",
          rows: entities.map(entity => entity.course)
        },
        {
          header: "Kredisi",
          rows: entities.map(entity => entity.credit)
        },
        {
          header: "Karnede Görünsün mü?",
          rows: entities.map(entity => entity.displayOnTheSchoolReport ? "Evet" : "Hayır")
        }
      ];
  
      const filename = "Sınıf Ders Listesi";
      exportToPDF(views, filename);
    });    
  }  

  /** Belli Bir Sınıf Ders Listesini Yazdırma Penceresini Açar */
  const printCourses = () => {
    dispatch(actions.getAllGradeCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Sınıf Ders Listesi";
      const properties = ["Sınıf", "Ders", "Kredisi", "Karnede Görünsün mü?"];
  
      entities.forEach(entity => {
        data.push({
          "Sınıf": entity.grade,
          "Ders": entity.course,
          "Kredisi": entity.credit,
          "Karnede Görünsün mü?": entity.displayOnTheSchoolReport ? "Evet" : "Hayır"
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Sınıf Ders Listesini Panoya Kopyalar */
  const copyCourses = () => {
    dispatch(actions.getAllGradeCourses(coursesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Sınıf Ders Listesi";
      const columns = ["Sınıf", "Ders", "Kredisi", "Karnede Görünsün mü?"];
      entities.forEach(entity => rows.push([entity.grade, entity.course, entity.credit, entity.displayOnTheSchoolReport ? "Evet" : "Hayır"]));
      copy({ header, columns, rows });
    });    
  }

  /** Belli Bir Sınıf Ders Listesini Siler */
  const deleteCourses = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }
  
    deletionAlert().then(result => {
      if(result.isConfirmed) dispatch(actions.deleteCourses(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);  
    if (isSuccess) setTimeout(() => dispatch(actions.getGradeCourses(coursesUIProps.queryParams)), 2500);
  };
  
  const title = <>Sınıf Ders Listesi{coursesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{coursesUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportCoursesToExcel}
                exportToPDF={exportCoursesToPDF}
                print={printCourses}
                copy={copyCourses}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Ders"
                method={coursesUIProps.openNewGradeCoursePage}
              />
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteCourses(coursesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <GradeCoursesFilter />
        <GradeCoursesTable
          deleteCourses={deleteCourses}
        />
      </CardBody>
    </Card>
  );
}